"use client"

import type { Post } from "@/sanity.types"
import { QuoteBox } from "components/QuoteBox"
import { YoutubeEmbed } from "components/YoutubeEmbed"
import UniversalImage from "library/UniversalImage"
import UniversalLink from "library/link"
import { TypedPortableText } from "library/sanity/PortableText"
import type { DeepAssetMeta } from "library/sanity/assetMetadata"
import { css, f, styled } from "library/styled"
import colors from "styles/colors"
import textStyles from "styles/text"

export default function BlogRich({
	className,
	value,
	title,
}: {
	className?: string
	value: DeepAssetMeta<Post["content"]>
	title?: string | null | undefined
}) {
	if (!value) return null
	return (
		<Wrapper className={className}>
			<Title>{title}</Title>
			<TypedPortableText
				value={value}
				components={{
					types: {
						image: ({ value }) => {
							return <BlogImage src={value} />
						},
						youtube: ({ value }) => {
							return <YoutubeEmbed video={value} />
						},
						quotebox: ({ value }) => {
							return <QuoteBox data={value} />
						},
					},
					block: {
						normal: ({ children }) => (
							<StyledParagraph>{children}</StyledParagraph>
						),
						h1: ({ children }) => <StyledH1>{children}</StyledH1>,
						h2: ({ children }) => <StyledH2>{children}</StyledH2>,
						h3: ({ children }) => <StyledH3>{children}</StyledH3>,
						h4: ({ children }) => <StyledH4>{children}</StyledH4>,
					},
					list: {
						bullet: ({ children }) => (
							<StyledBulletList>{children}</StyledBulletList>
						),
						number: ({ children }) => (
							<StyledNumberList>{children}</StyledNumberList>
						),
					},
					marks: {
						strong: ({ children }) => <StyledStrong>{children}</StyledStrong>,
						em: ({ children }) => <StyledEm>{children}</StyledEm>,
						underline: ({ children }) => (
							<StyledUnderline>{children}</StyledUnderline>
						),
						"strike-through": ({ children }) => (
							<StyledStrikeThrough>{children}</StyledStrikeThrough>
						),
						link: ({ value, children }) =>
							value.href ? (
								<StyledLink href={value.href}>{children}</StyledLink>
							) : (
								<>{children} (empty link)</>
							),
					},
				}}
			/>
		</Wrapper>
	)
}

const Title = styled("h1", {
	...f.responsive(css`
		${textStyles.h4};
		color: ${colors.green500};
		width: 100%;
	`),
	...f.mobile(css`
		${textStyles.h5};
	`),
})

const BlogImage = styled(UniversalImage, {
	...f.responsive(css`
		width: 100%;
		height: auto;
		border-radius: 16px;
	`),
})

const StyledParagraph = styled("p", {
	...f.responsive(css`
		${textStyles.bodyS};
		color: ${colors.green400};
	`),

	...f.tablet(css`
		${textStyles.bodyR};
	`),
})

const StyledH1 = styled("h1", {
	...f.responsive(css`
		${textStyles.h4};
		color: ${colors.green500};
	`),

	...f.mobile(css`
		${textStyles.h5};
	`),
})

const StyledH2 = styled("h2", {
	...f.responsive(css`
		${textStyles.h5};
		color: ${colors.green500};
	`),

	...f.mobile(css`
		${textStyles.h6};
		color: ${colors.green500};
	`),
})

const StyledH3 = styled("h3", {
	...f.responsive(css`
		${textStyles.bodyXL};
		color: ${colors.green500};
	`),

	...f.mobile(css`
		${textStyles.bodyL};
	`),
})

const StyledH4 = styled("h4", {
	...f.responsive(css`
		${textStyles.titleR};
		color: ${colors.green400};
	`),
})

const StyledBulletList = styled("ul", {
	...f.responsive(css`
		${textStyles.bodyS};
		color: ${colors.green400};
		list-style-type: disc;
		padding-left: 24px;

		ul {
			list-style-type: circle;
			margin-top: 8px;

			ul {
				list-style-type: square;

				ul {
					list-style-type: disc;
				}
			}
		}
	`),

	...f.tablet(css`
		${textStyles.bodyR};
	`),
})

const StyledNumberList = styled("ol", {
	...f.responsive(css`
		${textStyles.bodyS};
		color: ${colors.green400};
		list-style-type: decimal;
		padding-left: 24px;

		ol {
			list-style-type: lower-alpha;
			margin-top: 8px;

			ol {
				list-style-type: lower-roman;

				ol {
					list-style-type: upper-alpha;
				}
			}
		}
	`),

	...f.tablet(css`
		${textStyles.bodyR};
	`),
})

const StyledStrong = styled("strong", {
	...f.responsive(css`
		font-weight: bold;
		color: ${colors.green500};
	`),
})
const StyledEm = styled("em", {
	...f.responsive(css`
		font-style: italic;
		color: ${colors.green400};
	`),
})

const StyledUnderline = styled("u", {
	...f.responsive(css`
		text-decoration: underline;
		color: ${colors.green500};
	`),
})
const StyledStrikeThrough = styled("s", {
	...f.responsive(css`
		text-decoration: line-through;
		color: ${colors.green400};
	`),
})
const StyledLink = styled(UniversalLink, {
	...f.responsive(css`
		color: ${colors.green200};
		text-decoration: underline;

		&:hover {
			color: ${colors.green500};
		}
	`),
})

const Wrapper = styled("div", {
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		gap: 40px;
	`),
})
